import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Rocket,
  Boxes,
  GitBranch,
  LineChart,
  ShieldCheck,
  Bot,
  Zap,
  Cloud,
  Settings,
  Database,
  Cpu,
  Globe,
} from 'lucide-react';
import StatusBadge, { CapabilityStatus } from './StatusBadge';

const capabilities: {
  icon: typeof Rocket;
  title: string;
  description: string;
  status: CapabilityStatus;
  color: string;
}[] = [
  {
    icon: Rocket,
    title: 'Deploy',
    description:
      'A consistent workflow for deploying applications and infrastructure, instead of a different process for every environment.',
    status: 'in-development',
    color: 'text-neon-blue',
  },
  {
    icon: Boxes,
    title: 'Kubernetes',
    description:
      'Understand and operate Kubernetes workloads, clusters, and resources from one interface.',
    status: 'in-development',
    color: 'text-neon-purple',
  },
  {
    icon: GitBranch,
    title: 'CI/CD',
    description:
      'Manage deployment workflows, pipeline triggers, and release operations alongside the infrastructure they ship to.',
    status: 'coming-soon',
    color: 'text-neon-cyan',
  },
  {
    icon: LineChart,
    title: 'Observability',
    description:
      'Bring metrics, logs, events, and alerts together in one place to understand system health.',
    status: 'coming-soon',
    color: 'text-green-400',
  },
  {
    icon: ShieldCheck,
    title: 'Security',
    description:
      'Surface infrastructure and deployment risks before they become production incidents.',
    status: 'coming-soon',
    color: 'text-orange-400',
  },
  {
    icon: Bot,
    title: 'AI Operations',
    description:
      'Use AI to understand infrastructure state, investigate issues, and assist with operational decisions.',
    status: 'in-development',
    color: 'text-neon-blue',
  },
];

const technologies = [
  { name: 'Kubernetes', icon: Cloud, color: 'text-blue-400' },
  { name: 'Docker', icon: Database, color: 'text-blue-500' },
  { name: 'GitOps', icon: GitBranch, color: 'text-purple-400' },
  { name: 'Helm', icon: Settings, color: 'text-green-400' },
  { name: 'CI/CD', icon: Zap, color: 'text-orange-400' },
  { name: 'AWS', icon: Globe, color: 'text-yellow-400' },
  { name: 'GCP', icon: Cpu, color: 'text-red-400' },
  { name: 'Azure', icon: ShieldCheck, color: 'text-blue-600' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const Features: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="capabilities" className="py-32 relative overflow-hidden bg-dark-950">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 glass-premium rounded-full px-6 py-3 mb-8"
          >
            <Zap className="w-5 h-5 text-neon-blue" />
            <span className="text-sm font-medium text-gray-300">Platform Capabilities</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-6xl font-semibold mb-6"
          >
            <span className="text-white">One platform for how you</span>
            <br />
            <span className="gradient-text">deploy and operate infrastructure</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-gray-400 max-w-3xl mx-auto"
          >
            DeploAI is being built around the workflows engineering teams already run. Some
            capabilities are in active development today; others are on the roadmap and clearly
            labeled below.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {capabilities.map((capability) => (
            <motion.div key={capability.title} variants={itemVariants} className="feature-card group">
              <div className="flex items-start justify-between mb-6">
                <div
                  className={`w-16 h-16 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 neon-glow`}
                >
                  <capability.icon className="w-8 h-8 text-white" />
                </div>
                <StatusBadge status={capability.status} />
              </div>

              <h3 className={`text-2xl font-semibold mb-4 ${capability.color} group-hover:text-white transition-colors duration-300`}>
                {capability.title}
              </h3>

              <p className="text-gray-400 leading-relaxed">{capability.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Ecosystem compatibility */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass-premium rounded-3xl p-12 text-center"
        >
          <h3 className="text-3xl font-semibold mb-6 text-white">
            Built for the infrastructure beneath your applications
          </h3>

          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            DeploAI is designed to complement the systems engineering teams already run — not
            replace them. Target ecosystem support:
          </p>

          <div className="flex flex-wrap justify-center items-center gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                className={`px-6 py-3 glass rounded-full ${tech.color} font-medium hover:scale-105 transition-all duration-300 flex items-center space-x-2`}
              >
                <tech.icon className="w-5 h-5" />
                <span>{tech.name}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
